/**
 * Convite de push — o registro de "este aparelho já foi perguntado".
 *
 * ## Por que a dispensa é DEFINITIVA — ✕ e "Agora não"
 *
 * O que este convite abre, ao apertar "Ativar", é o diálogo NATIVO de
 * permissão, e ele é **one-shot por origem**. Insistir depois de um "não"
 * lido de verdade não dá segunda chance; dá "Bloquear" com uma decisão que
 * nem o app nem o usuário conseguem desfazer de dentro do produto. Por isso
 * tocar em ✕ ou em "Agora não" — os dois controles DENTRO da folha, que
 * exigem ter notado o convite e mirado num alvo pequeno — fecha para sempre
 * neste aparelho. Depois sobra exatamente um caminho, e ele nunca some:
 * Ajustes › Privacidade → Notificações (`SettingsNotificationsSection`).
 *
 * ## Por que tocar fora / arrastar pra fechar ganham uma segunda chamada
 *
 * Medido em 11/09: 71% dos elegíveis (100/140) não tinham NENHUM sinal de
 * push — nem token, nem `notificationsOptIn` — e não dava pra saber quanto
 * disso era decisão de verdade, porque as três saídas (✕, "Agora não" e o
 * scrim/arrastar) chamavam a mesma função e nada ficava gravado. Diferente de
 * apertar um botão específico, tocar no scrim ou arrastar a folha pra baixo é
 * o reflexo comum de "fechar qualquer coisa" em sheet mobile — uma leitura de
 * intenção mais fraca, não uma resposta ao convite. Essas duas saídas ganham
 * UMA reoferta, `SOFT_DISMISS_REOFFER_MS` depois, e só enquanto o diálogo do
 * sistema ainda não rodou (`permission === "default"` — reoferecer depois de
 * `granted`/`denied` seria inútil ou pior, ver `pushPlatformGate.ts`). Se a
 * pessoa sair de novo pelo scrim/arrastar DEPOIS da reoferta, a folha já deu
 * a chance — essa segunda saída também vira definitiva.
 *
 * ## Por que a chave é do DEVICE, e não do usuário
 *
 * `Notification.permission` é por origem+aparelho, não por conta. Amarrar a
 * dispensa ao `uid` faria o convite reaparecer a cada troca de conta no mesmo
 * telefone, perguntando de novo algo que aquele browser já respondeu.
 *
 * ## Storage indisponível (Safari privado, quota)
 *
 * A escrita falha em silêncio e a leitura devolve `false` — o convite volta na
 * PRÓXIMA sessão. Dentro da sessão corrente, quem segura é a flag de módulo:
 * o pior caso vira "reaparece amanhã", nunca "reaparece na próxima navegação".
 */

import {isNativeApp} from "../../../lib/platform/native";
import {isPushPromptAllowedOnPlatform} from "./pushPlatformGate";

const DISMISS_KEY = "livra_push_prompt_dismissed";

/* ─────────────────────────────────────────────────────────────────────────
 * TETO DE REAPRESENTAÇÃO — estado NOSSO, não do usuário
 *
 * A dispensa acima é uma DECISÃO DA PESSOA ("agora não") e é definitiva. Isto
 * aqui é outra coisa: o app desistindo de insistir depois de falhar sozinho.
 * Os dois nunca se confundem — chaves separadas, funções separadas, e teste
 * provando que mexer num não mexe no outro.
 *
 * ## Por que precisa existir
 *
 * `granted` sem token pode ser PERMANENTE, e no web a trava de plataforma não
 * age: service worker que não registra, VAPID errado no build, navegador
 * bloqueando push por política. Nesses casos `canOfferPushPrompt` devolve
 * `true` para sempre — o convite reabre a cada sessão, para alguém que **já
 * disse sim**. Insistir não conserta e irrita.
 *
 * ⚠️ E não há rede por baixo: `FcmTokenSync` só roda com
 * `notificationsOptIn === true`, campo que apenas `registerFcmToken` grava — e
 * ele só roda COM token. Ou seja, quem está em `granted` sem token nunca teve
 * o opt-in gravado e **não tem recuperação silenciosa**. O sheet é o único
 * caminho automático, e é justamente por isso que ele precisa de um fim.
 *
 * ## Por que 2
 *
 * As causas se dividem em duas famílias:
 *  · TRANSITÓRIA — rede caiu no instante do `getToken`, SW ainda registrando,
 *    timeout. Uma segunda chance resolve: na sessão seguinte o SW está pronto.
 *  · PERMANENTE — VAPID errado, SW 404, política do navegador. Aqui **nenhum**
 *    N resolve: 2 ou 20 falham igual, e cada tentativa é uma interrupção para
 *    quem já concedeu.
 *
 * Então o N útil é o menor que cobre a família transitória. 1 seria cedo
 * demais (a primeira falha pode ser a rede); 3 só ajudaria se a falha fosse
 * intermitente perto de 50%, e nesse caso a pessoa não teria como entender por
 * que o app insiste. Custo máximo do defeito permanente: **duas aparições**.
 *
 * Esgotado o teto, o caminho manual continua e é o que a copy da folha já
 * anuncia: Ajustes › Privacidade → Notificações.
 * ───────────────────────────────────────────────────────────────────────── */

const TOKEN_FAILURE_KEY = "livra_push_token_failures";

/** Ver o bloco acima para o critério. */
export const MAX_PUSH_TOKEN_ATTEMPTS = 2;

/** Espelho em memória — cobre `localStorage` inacessível, igual à dispensa. */
let tokenFailuresInSession = 0;

function readTokenFailures(): number {
  let stored = 0;
  try {
    stored = Number(window.localStorage.getItem(TOKEN_FAILURE_KEY)) || 0;
  } catch {
    /* modo privado / quota */
  }
  return Math.max(stored, tokenFailuresInSession);
}

/**
 * Registra que o `getToken` falhou COM a permissão concedida.
 *
 * ⚠️ Só para esse caso. Recusa no diálogo é dispensa (o browser não deixa
 * repetir) e permissão em `default` não é falha — é alguém que ainda não
 * respondeu.
 */
export function recordPushTokenFailure(): void {
  const proximo = readTokenFailures() + 1;
  tokenFailuresInSession = proximo;
  try {
    window.localStorage.setItem(TOKEN_FAILURE_KEY, String(proximo));
  } catch {
    /* a flag de módulo segura a sessão */
  }
}

/** O app já insistiu o suficiente e deve parar de oferecer sozinho. */
export function hasExhaustedPushTokenAttempts(): boolean {
  return readTokenFailures() >= MAX_PUSH_TOKEN_ATTEMPTS;
}

/** Espelho em memória — cobre o caso de `localStorage` inacessível. */
let dismissedInSession = false;

/** Dispensa DEFINITIVA — ✕, "Agora não", ou a segunda saída pelo scrim/arrastar. */
export function dismissPushPrompt(): void {
  dismissedInSession = true;
  try {
    window.localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // Modo privado / storage cheio: a flag de módulo segura a sessão.
  }
}

export function isPushPromptDismissed(): boolean {
  if (dismissedInSession) return true;
  try {
    return window.localStorage.getItem(DISMISS_KEY) !== null;
  } catch {
    return false;
  }
}

/* ─────────────────────────────────────────────────────────────────────────
 * SEGUNDA CHAMADA — saída pelo scrim ou por arrastar a folha
 *
 * Ver o docblock do topo ("Por que tocar fora / arrastar ganham uma segunda
 * chamada"). Chave e janela separadas da dispensa definitiva de propósito: a
 * primeira saída por aqui não é "não", é "ainda não decidi por essa via" — e
 * só a SEGUNDA, depois da reoferta, é que vira a mesma dispensa de sempre.
 * ───────────────────────────────────────────────────────────────────────── */

const SOFT_DISMISS_KEY = "livra_push_prompt_soft_dismissed_at";

/** 7 dias — cadência de reoferta de baixa fricção, mesma ordem de grandeza do convite de instalação (30d, mas este é mais barato: não reabre diálogo nenhum sozinho). */
export const SOFT_DISMISS_REOFFER_MS = 7 * 24 * 60 * 60 * 1000;

/** Espelho em memória — cobre `localStorage` inacessível, igual à dispensa. */
let softDismissedAtInSession: number | null = null;

function readSoftDismissedAt(): number | null {
  if (softDismissedAtInSession !== null) return softDismissedAtInSession;
  try {
    const raw = window.localStorage.getItem(SOFT_DISMISS_KEY);
    const parsed = raw === null ? null : Number(raw);
    return parsed && Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeSoftDismissedAt(tsMs: number): void {
  softDismissedAtInSession = tsMs;
  try {
    window.localStorage.setItem(SOFT_DISMISS_KEY, String(tsMs));
  } catch {
    // a flag de módulo segura a sessão
  }
}

/**
 * Saída pelo scrim (tocar fora) ou pelo gesto de arrastar a folha pra baixo —
 * NUNCA pelos botões ✕/"Agora não", que chamam `dismissPushPrompt` direto.
 *
 * 1ª vez: registra o instante e NÃO dispensa — `canOfferPushPrompt` volta a
 * liberar depois de `SOFT_DISMISS_REOFFER_MS`, e só então a folha pode
 * reaparecer. 2ª vez (só possível depois da reoferta, já que antes dela o
 * gate nem reabre a folha): a chance já foi dada — escala para a dispensa
 * definitiva de `dismissPushPrompt`.
 */
export function dismissPushPromptSoft(): void {
  if (readSoftDismissedAt() === null) {
    writeSoftDismissedAt(Date.now());
    return;
  }
  dismissPushPrompt();
}

/** Dispensa efetiva agora — definitiva, OU suave e ainda dentro da janela de espera. */
function isPushPromptDismissedForNow(): boolean {
  if (isPushPromptDismissed()) return true;
  const softAt = readSoftDismissedAt();
  if (softAt === null) return false;
  return Date.now() - softAt < SOFT_DISMISS_REOFFER_MS;
}

/**
 * Vale a pena oferecer o convite neste runtime?
 *
 * Checagem barata e SÍNCRONA de propósito — roda em toda renderização do
 * watcher, então não pode importar `firebase/messaging` nem tocar em rede. O
 * suporte real (`isFcmSupported`) é verificado só depois que isto passa.
 *
 *  - `granted`  → não há o que pedir;
 *  - `denied`   → o browser não deixa repetir o pedido; o convite seria um
 *                 botão mentiroso (classe do BUG-58);
 *  - dispensado → definitivo (✕/"Agora não"), ou suave e ainda dentro da
 *                 janela de reoferta (scrim/arrastar) — ver
 *                 `dismissPushPromptSoft`.
 */
export function canOfferPushPrompt(args?: {
  hasRegisteredToken?: boolean;
  /**
   * Estado de permissão lido por fora (`getNotificationPermissionState`).
   *
   * Existe por causa do app NATIVO: lá `Notification` não existe no WKWebView
   * e a leitura real é assíncrona (plugin) — impossível dentro desta função,
   * que é síncrona de propósito. O gate (`usePushPromptGate`) injeta o valor
   * fresco no recheck que roda depois do assentamento; o pré-check síncrono
   * roda sem ele e decide só pelo que sabe (plataforma + dispensa).
   */
  permission?: NotificationPermission;
}): boolean {
  /*
   * Plataforma ANTES de tudo. É a trava de segurança de produto: pedir
   * permissão onde o token não registra gasta a chance única da pessoa num
   * "sim" que não vira canal nenhum. Ver `pushPlatformGate.ts`.
   */
  if (!isPushPromptAllowedOnPlatform()) return false;

  /*
   * De onde vem a permissão, em ordem: injetada pelo chamador → API web →
   * desconhecida. "Desconhecida" só é estado legítimo no NATIVO (pré-check
   * síncrono, antes de o plugin responder) — ali seguimos só com dispensa, e
   * quem dá o veredito final é o recheck do gate, já com o valor injetado. Na
   * web, runtime sem `Notification` não tem o que pedir: é o iOS Safari fora
   * da PWA, e o convite certo lá é o de instalação.
   */
  const permission =
    args?.permission ??
    (typeof Notification !== "undefined" ? Notification.permission : null);
  if (permission === null) {
    if (!isNativeApp()) return false;
    return !isPushPromptDismissedForNow();
  }

  if (permission === "denied") return false;

  /*
   * ⚠️ `granted` NÃO significa "resolvido". No iOS sem capability de APNs a
   * permissão fica concedida e o token nunca registra — e como este gate
   * barrava por `permission !== "default"`, a pessoa ficava contada como quem
   * disse sim, sem receber nada, para sempre.
   *
   * A prova é o TOKEN, não a permissão. Concedido sem token, o estado é
   * RECUPERÁVEL e o convite pode voltar — e voltar aqui não custa a chance
   * única: com a permissão já concedida, o sistema não reabre o diálogo, então
   * a nova tentativa é só um novo `getToken`.
   *
   * `hasRegisteredToken` é omitido por chamadores que não têm o perfil em
   * mãos; nesse caso o comportamento antigo vale (granted = resolvido), que é
   * o lado conservador para quem não sabe.
   */
  if (permission === "granted") {
    if (args?.hasRegisteredToken !== false) return false;
    /*
     * Concedida e sem token: recuperável, mas não para sempre. Depois de
     * `MAX_PUSH_TOKEN_ATTEMPTS` falhas o app para de oferecer sozinho — ver o
     * bloco do teto acima. Isto NÃO é a dispensa do usuário: são estados
     * separados de propósito.
     */
    if (hasExhaustedPushTokenAttempts()) return false;
  }

  return !isPushPromptDismissedForNow();
}

/** Só para os testes — zera memória e espelho. */
export function __resetPushPromptForTests(): void {
  dismissedInSession = false;
  tokenFailuresInSession = 0;
  softDismissedAtInSession = null;
  try {
    window.localStorage.removeItem(DISMISS_KEY);
    window.localStorage.removeItem(TOKEN_FAILURE_KEY);
    window.localStorage.removeItem(SOFT_DISMISS_KEY);
  } catch {
    /* idem */
  }
}
